/**
 * `<DisableContactDialog>` — confirm before disabling a contact, with an
 * optional `reason` text field. Standard-friction tier per NFR-Design § 2.
 */

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
} from "@cometchat-ai/ui-dashboard";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";

export interface DisableContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactName: string;
  busy?: boolean;
  onConfirm: (reason: string | undefined) => void;
}

export function DisableContactDialog({
  open,
  onOpenChange,
  contactName,
  busy = false,
  onConfirm,
}: DisableContactDialogProps) {
  const { t } = useTranslation("contacts");
  const inputId = useId();
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!open) setReason("");
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="disable-contact-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">{t("disable.title")}</DialogTitle>
          <DialogDescription className="text-[13px]">
            {t("disable.body", { name: contactName })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor={inputId} className="text-[13px]">
            {t("disable.reasonLabel")}
          </Label>
          <Textarea
            id={inputId}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("disable.reasonPlaceholder")}
            rows={3}
            data-testid="disable-contact-reason"
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => onOpenChange(false)}
            data-testid="disable-contact-cancel"
          >
            {t("form.cancel")}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            disabled={busy}
            onClick={() => onConfirm(reason.trim() === "" ? undefined : reason.trim())}
            data-testid="disable-contact-confirm"
          >
            {t("disable.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
